import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@mui/material'
import InputField from 'components/form/InputField'
import SubmitButton from 'components/form/SubmitButton'
import InfiniteScrollTable from 'components/ui/InfiniteScrollTable'
import SingleRoundRow from 'components/game/SingleRoundRow'
import { selectGames } from 'store/games/slice'
import { searchGames } from 'store/games/thunks'

export default function GameSearch(): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { games, searchLimit, searchOffset, status } = useSelector(selectGames)
  const initialFormData = useMemo(() => {
    return { name: '', viewCode: '', gameCode: '' }
  }, [])
  const { register, handleSubmit, reset, getValues, watch } = useForm<GameSearchPayload>({
    defaultValues: initialFormData,
  })

  const isInputDisabled = useMemo(
    () => (name: string) => {
      const data = Object.entries(watch())
      const filtered = data.filter(([key, value]: any) => key !== name && value !== '')
      return filtered.length > 0
    },
    [watch],
  )

  const isSearchButtonDisabled = useMemo(() => {
    const data = Object.entries(watch())
    return data.filter(([value]: any) => value !== '').length === 0
  }, [watch])

  const submitSearchForm = (data: GameSearchPayload) => {
    const payload = {
      ...data,
      limit: searchLimit,
      offset: 0,
    }
    dispatch(searchGames(payload))
  }

  const clearFilters = () => {
    reset(initialFormData)
  }

  const columns: TableColumn[] = [
    {
      id: 'date',
      label: intl.formatMessage({ id: 'strings.date' }),
    },
    {
      id: 'name',
      label: intl.formatMessage({ id: 'strings.name' }),
    },
    {
      id: 'club',
      label: intl.formatMessage({ id: 'strings.club' }),
    },
    {
      id: 'course',
      label: intl.formatMessage({ id: 'strings.course' }),
    },
    {
      id: 'options',
      label: intl.formatMessage({ id: 'strings.options' }),
    },
    {
      id: 'creator',
      label: intl.formatMessage({ id: 'strings.createdBy' }),
    },
    {
      id: 'gameId',
      label: intl.formatMessage({ id: 'strings.gameId' }),
    },
    {
      id: 'roundId',
      label: intl.formatMessage({ id: 'strings.roundId' }),
    },
    {
      id: 'codes',
      label: intl.formatMessage({ id: 'strings.joinViewCode' }),
    },
  ]

  const getButtonStatus = () => {
    return games.length > 0 ? 'idle' : status
  }

  const getNoMoreRowsText = () => {
    return games.length > searchLimit ? intl.formatMessage({ id: 'strings.allRoundsFetched' }) : ''
  }

  const loadMoreRounds = () => {
    const data = getValues()
    const payload = {
      ...data,
      limit: searchLimit,
      offset: searchOffset,
    }
    dispatch(searchGames(payload))
  }

  return (
    <PageContainer>
      <PageTitle>{intl.formatMessage({ id: 'strings.searchGames' })}</PageTitle>
      <SearchForm onSubmit={handleSubmit(submitSearchForm)}>
        <SearchFiltersContainer>
          <InputField
            register={register}
            name="name"
            type="text"
            label={intl.formatMessage({ id: 'strings.name' })}
            outlined
            autoFocus
            disabled={isInputDisabled('name')}
          />
          <InputField
            register={register}
            name="gameCode"
            type="text"
            label={intl.formatMessage({ id: 'strings.joinCode' })}
            outlined
            disabled={isInputDisabled('gameCode')}
          />
          <InputField
            register={register}
            name="viewCode"
            type="text"
            label={intl.formatMessage({ id: 'strings.viewCode' })}
            outlined
            disabled={isInputDisabled('viewCode')}
          />
          <ActionButtons>
            <SearchButton
              status={getButtonStatus()}
              text={intl.formatMessage({ id: 'strings.search' })}
              disabled={isSearchButtonDisabled}
            />
            <ClearFilters onClick={clearFilters}>{intl.formatMessage({ id: 'strings.clearFilters' })}</ClearFilters>
            {games?.length > 0 && (
              <SearchResultCount>
                {games.length} {intl.formatMessage({ id: 'strings.results' })}
              </SearchResultCount>
            )}
          </ActionButtons>
        </SearchFiltersContainer>

        {games.length > 0 && (
          <SeachResultsContainer>
            <InfiniteScrollTable
              columns={columns}
              rowCount={games.length}
              hasMoreRows={games.length % searchLimit === 0 && games.length >= searchLimit}
              loadMore={loadMoreRounds}
              noMoreRowsText={getNoMoreRowsText()}
            >
              {games.map((round: GameSearchRound, i: number) => (
                <SingleRoundRow key={i} round={round} />
              ))}
            </InfiniteScrollTable>
          </SeachResultsContainer>
        )}
      </SearchForm>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  margin: 10px;
  padding: 10px 20px 20px;
  min-height: 500px;
  background-color: #fff;
  width: 100%;
`

const PageTitle = styled.h1`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 26px;
`

const SearchForm = styled.form`
  padding: 20px 0;
`

const SearchButton = styled(SubmitButton)`
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
`

const ClearFilters = styled(Button)`
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.main};
`

const SeachResultsContainer = styled.div`
  & .MuiDataGrid-colCellTitleContainer {
    justify-content: flex-start !important;
  }
  & .MuiDataGrid-columnsContainer {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.customPalette.light};
    font-size: 0.85em;
    text-transform: uppercase;
  }
  & .MuiDataGrid-columnSeparator {
    display: none;
  }
  & a {
    color: ${(props) => props.theme.palette.primary.main};
  }
  & svg.MuiDataGrid-sortIcon {
    color: ${(props) => props.theme.customPalette.light};
  }
`
const SearchResultCount = styled.div`
  display: inline-block;
  padding-left: 20px;
  font-size: 0.9em;
`

const SearchFiltersContainer = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 50px;

  & > .MuiFormControl-root {
    width: 200px;
  }
`

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 20px 0 0 10px;
`
